/* eslint-disable */
/**
 * GENERATED FILE. DO NOT EDIT.
 * Source: shared/src/billing/personalLearning.ts
 * Edit the source, then run `node scripts/sharedSync.js`.
 * `npm run audit:all` fails while this copy differs from its source.
 */
/**
 * Paid personal learning rules shared by the web pages and the mobile screens.
 *
 * Two products: self-paced study on a subscription (monthly, or annual at a
 * discount), and a personal semester bought once for a start date the learner
 * chooses. The server decides what a learner may do; this module makes both
 * clients read its answers, price the plans and handle start dates the same
 * way, so a learner sees the same numbers and the same dates on either device.
 */
/** The code the server sends (HTTP 402) when self-paced study needs a plan. */
export const SELF_PACED_SUBSCRIPTION_REQUIRED = 'SELF_PACED_SUBSCRIPTION_REQUIRED';
/** Where both clients send a learner to book a personal semester. */
export const PERSONAL_SEMESTER_PATH = '/personal-semester';
/** True for the server's "self-paced study needs a plan" refusal. */
export function isSelfPacedPaywallError(error) {
    const response = error?.response;
    return !!response && response.data?.code === SELF_PACED_SUBSCRIPTION_REQUIRED;
}
/** Whether the paywall should replace the learning surfaces for this learner. */
export function selfPacedLocked(access) {
    return !!access && access.required === true && access.entitled === false;
}
/** "$119", "$107.89": whole dollars lose their cents, anything else keeps two. */
export function formatUsd(cents) {
    if (cents == null || !Number.isFinite(Number(cents)))
        return '';
    const value = Number(cents);
    return `$${(value / 100).toFixed(value % 100 === 0 ? 0 : 2)}`;
}
/** What paying for a year saves against twelve monthly payments. */
export function annualSavingsCents(monthlyCents, annualCents) {
    const monthly = Number(monthlyCents);
    const annual = Number(annualCents);
    if (!Number.isFinite(monthly) || !Number.isFinite(annual) || monthly <= 0 || annual <= 0)
        return 0;
    return Math.max(0, monthly * 12 - annual);
}
/** The free plan stops being a way to learn once self-paced study is paid for. */
export function plansToOffer(plans, selfPacedRequiresSubscription) {
    const list = Array.isArray(plans) ? plans : [];
    return selfPacedRequiresSubscription ? list.filter((plan) => plan.id !== 'free') : list;
}
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
function pad(value) {
    return String(value).padStart(2, '0');
}
/** A date's calendar day in the device's own time zone, as YYYY-MM-DD. */
export function localDateString(date = new Date()) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
/** YYYY-MM-DD to a local-midnight Date, or null when it is not a real day. */
export function parseDateString(value) {
    const match = DATE_PATTERN.exec(String(value || '').trim());
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
        return null;
    return date;
}
/** Move a YYYY-MM-DD day by a number of calendar days. */
export function addDaysToDateString(value, days) {
    const date = parseDateString(value);
    if (!date)
        return value;
    date.setDate(date.getDate() + days);
    return localDateString(date);
}
/**
 * The start dates a learner may pick: from their own today (never before the
 * server's earliest) to the server's latest. YYYY-MM-DD strings compare in
 * calendar order.
 */
export function startDateRange(offer, now = new Date()) {
    const today = localDateString(now);
    const min = offer.minStartDate && offer.minStartDate > today ? offer.minStartDate : today;
    const max = offer.maxStartDate || addDaysToDateString(min, 90);
    return { min, max };
}
export function isStartDateInRange(value, range) {
    return !!parseDateString(value) && value >= range.min && value <= range.max;
}
/** Keep a picked date inside the allowed range. */
export function clampStartDate(value, range) {
    if (!parseDateString(value) || value < range.min)
        return range.min;
    if (value > range.max)
        return range.max;
    return value;
}
/** The last day of a semester that starts on `startDate` and runs `durationDays`. */
export function semesterEndDate(startDate, durationDays) {
    return addDaysToDateString(startDate, Math.max(1, Math.round(durationDays)) - 1);
}
/**
 * Show a server date (stored as midnight UTC on the chosen day) as the
 * calendar day it names, not shifted by the device's time zone.
 */
export function calendarDayOf(value) {
    if (!value)
        return null;
    if (typeof value === 'string' && DATE_PATTERN.test(value))
        return parseDateString(value);
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime()))
        return null;
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}
/** The YYYY-MM-DD a server date names, for prefilling a date field. */
export function calendarDayString(value) {
    const day = calendarDayOf(value);
    return day ? localDateString(day) : '';
}
/**
 * The last day of study for a stored semester. The server keeps the end as the
 * moment instruction stops (midnight after the last day); learners think in
 * last days.
 */
export function semesterLastDay(endValue) {
    const end = calendarDayString(endValue);
    return end ? addDaysToDateString(end, -1) : '';
}
/**
 * A personal semester has no public cohort page, so anything that would open
 * a cohort's details opens the personal semester page instead.
 */
export function isPersonalSemester(item) {
    return !!item && (item.scope === 'personal' || item.programType === 'personal_semester');
}
/** The learner's personal semester that has not finished, if any. */
export function liveSemester(semesters) {
    return (Array.isArray(semesters) ? semesters : []).find((item) => (!item.hasEnded && (item.enrollmentStatus === 'enrolled' || item.enrollmentStatus === 'active'))) || null;
}
